import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { formatGhanaCedis } from '../utils/formatUtils';
import { doc, addDoc, collection, query, where, orderBy, onSnapshot, updateDoc, getDocs, limit } from 'firebase/firestore';
import { db } from '../firebase/config';
import { WithdrawalRequest } from '../types';
import CustomDropdown from '../components/CustomDropdown';
import {
  CurrencyDollarIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  ArrowLeftIcon,
  CreditCardIcon,
  DevicePhoneMobileIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import AIChatButton from '../components/AIChatButton';

const WithdrawalPage: React.FC = () => {
  const { currentUser, updateProfile } = useAuth();
  const [selectedMethod, setSelectedMethod] = useState<string>('');
  const [amount, setAmount] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [withdrawalHistory, setWithdrawalHistory] = useState<WithdrawalRequest[]>([]);
  const [allPendingWithdrawals, setAllPendingWithdrawals] = useState<WithdrawalRequest[]>([]);

  const isAdmin = currentUser?.role === 'admin';

  // Utility function to convert Firestore timestamp to Date
  const convertTimestamp = (timestamp: any): Date => {
    if (timestamp instanceof Date) return timestamp;
    if (timestamp && typeof timestamp === 'object' && timestamp.seconds) {
      return new Date(timestamp.seconds * 1000);
    }
    if (timestamp && typeof timestamp === 'string') {
      return new Date(timestamp);
    }
    return new Date();
  };

  // Utility function to process withdrawal data
  const processWithdrawalData = (doc: any): WithdrawalRequest => {
    const data = doc.data();
    return {
      id: doc.id,
      userId: data.userId,
      amount: data.amount,
      status: data.status,
      method: data.method,
      accountDetails: data.accountDetails || {},
      createdAt: convertTimestamp(data.createdAt),
      updatedAt: convertTimestamp(data.updatedAt),
      processedAt: data.processedAt ? convertTimestamp(data.processedAt) : undefined,
      reason: data.reason,
      adminNotes: data.adminNotes
    };
  };

  // Fetch withdrawal history from Firestore
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!currentUser?.uid) return;
      try {
        const withdrawalsRef = collection(db, 'withdrawals');
        const q = query(
          withdrawalsRef,
          where('userId', '==', currentUser.uid),
          orderBy('createdAt', 'desc'),
          limit(50)
        );
        const snapshot = await getDocs(q);
        if (cancelled) return;
        const withdrawals: WithdrawalRequest[] = [];
        snapshot.forEach((doc) => {
          withdrawals.push(processWithdrawalData(doc));
        });
        setWithdrawalHistory(withdrawals);
      } catch (error) {
        console.error('Failed to load withdrawal history:', error);
        toast.error('Failed to load withdrawal history');
      }
    };
    run();
    return () => { cancelled = true; };
  }, [currentUser?.uid]);

  // Fetch all pending withdrawals for admin users
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!isAdmin) return;
      try {
        const withdrawalsRef = collection(db, 'withdrawals');
        const pendingQuery = query(
          withdrawalsRef,
          where('status', '==', 'pending'),
          orderBy('createdAt', 'desc'),
          limit(100)
        );
        const snapshot = await getDocs(pendingQuery);
        if (cancelled) return;
        const pending: WithdrawalRequest[] = [];
        snapshot.forEach((doc) => {
          pending.push(processWithdrawalData(doc));
        });
        setAllPendingWithdrawals(pending);
      } catch (error) {
        console.error('Error fetching pending withdrawals:', error);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [isAdmin]);

  const withdrawalMethods = [
    {
      id: 'mobile_money',
      name: 'Mobile Money',
      type: 'mobile_money',
      description: 'Fast withdrawal to your mobile money account',
      minAmount: 10,
      maxAmount: 5000,
      fees: 0,
      processingTime: '2-4 hours',
      isActive: true,
      icon: DevicePhoneMobileIcon,
      color: 'bg-green-100 text-green-600'
    },
    {
      id: 'bank_transfer',
      name: 'Bank Transfer',
      type: 'bank_transfer',
      description: 'Direct transfer to your bank account',
      minAmount: 50,
      maxAmount: 10000,
      fees: 5,
      processingTime: '1-3 business days',
      isActive: true,
      icon: CreditCardIcon,
      color: 'bg-blue-100 text-blue-600'
    }
  ];

  const [formData, setFormData] = useState({
    mobileMoney: { provider: '', phoneNumber: '', name: '' },
    bankTransfer: { bankName: '', accountNumber: '', accountName: '' }
  });

  const handleMethodSelect = (methodId: string) => {
    setSelectedMethod(methodId);
    setAmount('');
  };

  const handleInputChange = (field: string, value: string) => {
    if (selectedMethod === 'mobile_money') {
      setFormData(prev => ({
        ...prev,
        mobileMoney: { ...prev.mobileMoney, [field]: value }
      }));
    } else if (selectedMethod === 'bank_transfer') {
      setFormData(prev => ({
        ...prev,
        bankTransfer: { ...prev.bankTransfer, [field]: value }
      }));
    }
  };

  const getSelectedMethod = () => {
    return withdrawalMethods.find(method => method.id === selectedMethod);
  };

  const validateForm = () => {
    const method = getSelectedMethod();
    if (!method) return false;

    const numAmount = parseFloat(amount);
    if (isNaN(numAmount) || numAmount < method.minAmount || numAmount > method.maxAmount) {
      toast.error(`Amount must be between ${formatGhanaCedis(method.minAmount)} and ${formatGhanaCedis(method.maxAmount)}`);
      return false;
    }

    if (numAmount > (currentUser?.earnings || 0)) {
      toast.error('Insufficient earnings for this withdrawal');
      return false;
    }

    // Validate method-specific fields
    if (selectedMethod === 'mobile_money') {
      if (!formData.mobileMoney.provider) {
        toast.error('Please select a mobile money provider');
        return false;
      }
      if (!formData.mobileMoney.name) {
        toast.error('Please enter the account holder name');
        return false;
      }
      if (!formData.mobileMoney.phoneNumber) {
        toast.error('Please enter the phone number');
        return false;
      }
    } else if (selectedMethod === 'bank_transfer') {
      if (!formData.bankTransfer.bankName || !formData.bankTransfer.accountNumber || !formData.bankTransfer.accountName) {
        toast.error('Please fill in all bank transfer details');
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm() || !currentUser?.uid) return;

    setIsSubmitting(true);
    try {
      // Create withdrawal request object
      const withdrawalRequest: Omit<WithdrawalRequest, 'id'> = {
        userId: currentUser.uid,
        amount: parseFloat(amount),
        status: 'pending',
        method: selectedMethod as 'mobile_money' | 'bank_transfer',
        accountDetails: selectedMethod === 'mobile_money' ? formData.mobileMoney :
                      formData.bankTransfer,
        createdAt: new Date(),
        updatedAt: new Date()
      };

      // Save to Firestore withdrawals collection
      await addDoc(collection(db, 'withdrawals'), withdrawalRequest);

      // Update user earnings (this should ideally be done in a transaction)
      if (currentUser) {
        await updateProfile({
          earnings: (currentUser.earnings || 0) - parseFloat(amount)
        });
      }

      toast.success('Withdrawal request submitted successfully!');
      setSelectedMethod('');
      setAmount('');
      setFormData({
        mobileMoney: { provider: '', phoneNumber: '', name: '' },
        bankTransfer: { bankName: '', accountNumber: '', accountName: '' }
      });
    } catch (error) {
      console.error('Error submitting withdrawal:', error);
      toast.error('Failed to submit withdrawal request');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <ClockIcon className="h-5 w-5 text-yellow-500" />;
      case 'approved':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'rejected':
        return <XCircleIcon className="h-5 w-5 text-red-500" />;
      case 'completed':
        return <CheckCircleIcon className="h-5 w-5 text-blue-500" />;
      default:
        return <ExclamationTriangleIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'completed':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const handleAdminAction = async (withdrawalId: string, action: 'approve' | 'reject', reason?: string) => {
    if (!isAdmin) return;

    try {
      const withdrawalRef = doc(db, 'withdrawals', withdrawalId);
      const updateData: Partial<WithdrawalRequest> = {
        status: action === 'approve' ? 'approved' : 'rejected',
        updatedAt: new Date(),
        processedAt: new Date()
      };

      if (action === 'reject' && reason) {
        updateData.reason = reason;
      }

      // Update withdrawal status
      await updateDoc(withdrawalRef, updateData);

      toast.success(`Withdrawal ${action}d successfully!`);
    } catch (error) {
      console.error(`Error ${action}ing withdrawal:`, error);
      toast.error(`Failed to ${action} withdrawal`);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center mb-4">
            <button
              onClick={() => window.history.back()}
              className="mr-4 p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeftIcon className="h-5 w-5" />
            </button>
            <h1 className="text-3xl font-bold text-gray-900">Withdraw Earnings</h1>
          </div>
          <p className="text-gray-600">
            Request a withdrawal of your earnings using your preferred method
          </p>
        </div>

        {/* Current Balance */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Available Balance</h2>
              <p className="text-3xl font-bold text-primary-600">
                {formatGhanaCedis(currentUser?.earnings || 0)}
              </p>
            </div>
            <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center">
              <CurrencyDollarIcon className="h-8 w-8 text-primary-600" />
            </div>
          </div>
        </div>

        {/* Admin: All Pending Withdrawals */}
        {isAdmin && allPendingWithdrawals.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">All Pending Withdrawals</h2>
            <div className="space-y-4">
              {allPendingWithdrawals.map((withdrawal) => (
                <div
                  key={withdrawal.id}
                  className="border border-gray-200 rounded-lg p-4"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="font-medium text-gray-900">User ID: {withdrawal.userId}</p>
                      <p className="text-sm text-gray-600">
                        Method: {withdrawal.method.replace('_', ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-semibold text-gray-900">
                        {formatGhanaCedis(withdrawal.amount)}
                      </p>
                      <p className="text-sm text-gray-500">
                        {withdrawal.createdAt instanceof Date ? withdrawal.createdAt.toLocaleDateString() : new Date(withdrawal.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  
                  {/* Account Details */}
                  <div className="bg-gray-50 rounded-lg p-3 mb-3">
                    <p className="text-sm font-medium text-gray-700 mb-2">Account Details:</p>
                    {withdrawal.method === 'mobile_money' && (
                      <div className="text-sm text-gray-600">
                        <p>Provider: {withdrawal.accountDetails.provider}</p>
                        <p>Name: {withdrawal.accountDetails.name}</p>
                        <p>Phone: {withdrawal.accountDetails.phoneNumber}</p>
                      </div>
                    )}
                    {withdrawal.method === 'bank_transfer' && (
                      <div className="text-sm text-gray-600">
                        <p>Bank: {withdrawal.accountDetails.bankName}</p>
                        <p>Account: {withdrawal.accountDetails.accountNumber}</p>
                        <p>Name: {withdrawal.accountDetails.accountName}</p>
                      </div>
                    )}
                  </div>

                  {/* Admin Actions */}
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleAdminAction(withdrawal.id, 'approve')}
                      className="px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors"
                    >
                      Approve
                    </button>
                    <button
                      onClick={() => {
                        const reason = prompt('Enter rejection reason:');
                        if (reason) {
                          handleAdminAction(withdrawal.id, 'reject', reason);
                        }
                      }}
                      className="px-4 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 transition-colors"
                    >
                      Reject
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Withdrawal Form */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Request Withdrawal</h2>

            {/* Method Selection */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Select Withdrawal Method
              </label>
              <div className="space-y-3">
                {withdrawalMethods.map((method) => (
                  <div
                    key={method.id}
                    className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                      selectedMethod === method.id
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => handleMethodSelect(method.id)}
                  >
                    <div className="flex items-center">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center mr-3 ${method.color}`}>
                        <method.icon className="h-5 w-5" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-medium text-gray-900">{method.name}</h3>
                        <p className="text-sm text-gray-600">{method.description}</p>
                        <div className="flex items-center mt-2 text-xs text-gray-500">
                          <span>Min: {formatGhanaCedis(method.minAmount)}</span>
                          <span className="mx-2">•</span>
                          <span>Max: {formatGhanaCedis(method.maxAmount)}</span>
                          <span className="mx-2">•</span>
                          <span>{method.processingTime}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Withdrawal Form */}
            {selectedMethod && (
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Amount Input */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Amount to Withdraw
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">
                      GH₵
                    </span>
                    <input
                      type="number"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="Enter amount"
                      step="0.01"
                      min={getSelectedMethod()?.minAmount}
                      max={getSelectedMethod()?.maxAmount}
                    />
                  </div>
                  <p className="text-sm text-gray-500 mt-1">
                    Available: {formatGhanaCedis(currentUser?.earnings || 0)}
                  </p>
                </div>

                {/* Method-specific fields */}
                {selectedMethod === 'mobile_money' && (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Mobile Money Provider
                      </label>
                      <CustomDropdown
                        value={formData.mobileMoney.provider}
                        onChange={(value) => handleInputChange('provider', value)}
                        options={[
                          { value: "", label: "Select provider" },
                          { value: "mtn", label: "MTN Mobile Money" },
                          { value: "vodafone", label: "Vodafone Cash" },
                          { value: "airtel", label: "Airtel Money" }
                        ]}
                        placeholder="Select provider"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Account Holder Name
                      </label>
                      <input
                        type="text"
                        value={formData.mobileMoney.name}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder="Enter account holder name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        value={formData.mobileMoney.phoneNumber}
                        onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder="Enter phone number"
                      />
                    </div>
                  </div>
                )}

                {selectedMethod === 'bank_transfer' && (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Bank Name
                      </label>
                      <input
                        type="text"
                        value={formData.bankTransfer.bankName}
                        onChange={(e) => handleInputChange('bankName', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder="Enter bank name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Account Number
                      </label>
                      <input
                        type="text"
                        value={formData.bankTransfer.accountNumber}
                        onChange={(e) => handleInputChange('accountNumber', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder="Enter account number"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Account Name
                      </label>
                      <input
                        type="text"
                        value={formData.bankTransfer.accountName}
                        onChange={(e) => handleInputChange('accountName', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder="Enter account name"
                      />
                    </div>
                  </div>
                )}

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-primary-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-primary-700 focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isSubmitting ? 'Processing...' : 'Submit Withdrawal Request'}
                </button>
              </form>
            )}
          </div>

          {/* Withdrawal History */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Withdrawal History</h2>
            
            {withdrawalHistory.length === 0 ? (
              <div className="text-center py-8">
                <CurrencyDollarIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">No withdrawal requests yet</p>
              </div>
            ) : (
              <div className="space-y-4">
                {withdrawalHistory.map((withdrawal) => (
                  <div
                    key={withdrawal.id}
                    className="border border-gray-200 rounded-lg p-4"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center">
                        {getStatusIcon(withdrawal.status)}
                        <span className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(withdrawal.status)}`}>
                          {withdrawal.status.charAt(0).toUpperCase() + withdrawal.status.slice(1)}
                        </span>
                      </div>
                      <span className="text-lg font-semibold text-gray-900">
                        {formatGhanaCedis(withdrawal.amount)}
                      </span>
                    </div>
                    <div className="text-sm text-gray-600">
                      <p>Method: {withdrawal.method.replace('_', ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}</p>
                      <p>Date: {withdrawal.createdAt instanceof Date ? withdrawal.createdAt.toLocaleDateString() : new Date(withdrawal.createdAt).toLocaleDateString()}</p>
                      {withdrawal.status === 'rejected' && withdrawal.reason && (
                        <p className="text-red-600 mt-1">Reason: {withdrawal.reason}</p>
                      )}
                    </div>
                    
                    {/* Admin Actions */}
                    {isAdmin && withdrawal.status === 'pending' && (
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleAdminAction(withdrawal.id, 'approve')}
                            className="px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 transition-colors"
                          >
                            Approve
                          </button>
                          <button
                            onClick={() => {
                              const reason = prompt('Enter rejection reason:');
                              if (reason) {
                                handleAdminAction(withdrawal.id, 'reject', reason);
                              }
                            }}
                            className="px-3 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700 transition-colors"
                          >
                            Reject
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      {/* AI Chat Button */}
      <AIChatButton />
    </div>
  );
};

export default WithdrawalPage;
